import React from 'react';
import { cn } from '@/shared/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning' | 'primary';
  size?: 'sm' | 'default' | 'lg' | 'icon';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

// Base classes for all custom variants (without size-specific classes)
const baseClasses = 'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

// Custom variant styles
const customVariantStyles = {
  default: 'bg-primary text-white hover:bg-primary/90',
  primary: 'bg-primary text-white hover:bg-primary/90',
  secondary: 'bg-secondary text-white hover:bg-secondary/90',
  outline: 'border border-gray-300 bg-white hover:bg-gray-50',
  ghost: 'bg-transparent hover:bg-gray-100',
  danger: 'bg-danger text-white hover:bg-danger/90',
  success: 'bg-success text-white hover:bg-success/90',
  warning: 'bg-warning text-white hover:bg-warning/90',
} as const;

// Size styles
const sizeStyles = {
  sm: 'h-8 px-3 py-1 text-xs',
  default: 'h-10 px-4 py-2 text-sm',
  lg: 'h-12 px-6 py-3 text-base',
  icon: 'h-10 w-10 p-0',
} as const;

// Check if variant should use custom styling
const isCustomVariant = (variant: ButtonProps['variant']): variant is keyof typeof customVariantStyles => {
  return variant !== undefined && variant in customVariantStyles;
};



export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'default', 
    size = 'default', 
    loading = false, 
    leftIcon, 
    rightIcon,
    disabled,
    children,
    ...props 
  }, ref) => {
    const startContent = loading ? (
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    ) : leftIcon;

    // Get variant and size styles
    const variantStyles = isCustomVariant(variant) 
      ? customVariantStyles[variant] 
      : customVariantStyles.default;
    
    const sizeClasses = sizeStyles[size];

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          baseClasses,
          variantStyles,
          sizeClasses,
          className
        )}
        {...props}
      >
        {startContent}
        {children}
        {rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';